/**
 * Copyright Highway9 Networks Inc.
 *
 * Web half of the native <-> web ZIVA bridge (MCS-3994).
 *
 * The Highway9 mobile app embeds this app's `/mobile/ziva?embed=mobile` route in a
 * WebView and drives it over a small message contract. The authoritative contract is
 * `src/components/Ziva/zivaBridge.ts` in the h9mobile repo; the shapes below MUST stay
 * in lockstep with it (bump ZIVA_BRIDGE_VERSION on any breaking change to either side).
 *
 * Direction naming here is from the WEB's point of view:
 *   - NativeToWeb: the app injects these via `window.__zivaBridge.receive(msg)`.
 *   - WebToNative: we post these via `window.ReactNativeWebView.postMessage(JSON)`.
 *
 * The app seeds the CSP Bearer token into `window.__ZIVA_BOOTSTRAP__` before this SPA
 * boots; the token is never in the URL.
 */

export const ZIVA_BRIDGE_VERSION = 1;

/** native -> web (app injects these) */
export type ZivaNativeToWebMessage =
  | { type: "ziva:init"; token: string; cspUrl: string; insets?: ZivaInsets }
  | { type: "ziva:token"; token: string }
  | { type: "ziva:logout" }
  | { type: "ziva:back" };

/** web -> native (we post these) */
export type ZivaWebToNativeMessage =
  | { type: "ziva:ready" }
  | { type: "ziva:token-expired" }
  | { type: "ziva:close" }
  | { type: "ziva:unread"; count: number }
  | { type: "ziva:session"; id: string }
  | { type: "ziva:error"; code?: string; message?: string };

export type ZivaInsets = { top: number; bottom: number };

export type ZivaBootstrap = {
  token: string;
  cspUrl: string;
  insets?: ZivaInsets;
};

declare global {
  interface Window {
    /** Present only inside the RN WebView; used to post messages back to the app. */
    ReactNativeWebView?: { postMessage: (message: string) => void };
    /** Seeded by the app before this SPA boots (document-start). */
    __ZIVA_BOOTSTRAP__?: unknown;
    /** Installed by us so the app can push native -> web messages. */
    __zivaBridge?: { receive: (message: unknown) => void };
  }
}

// Latched on the embed route so cross-cutting code (e.g. the global 401 handler) can
// change behaviour without importing React state. Set once, early, by MobileZivaPage.
let embedActive = false;

export function enableMobileHostMode(): void {
  // Only latch inside the app. A plain-browser visit to /mobile/ziva would otherwise keep
  // this set for the rest of the SPA session, and a later 401 would post `ziva:token-expired`
  // to nobody instead of redirecting to /login, stranding the user on a dead session.
  if (typeof window === "undefined" || !window.ReactNativeWebView) return;
  embedActive = true;
}

/** Whether we are running as the app-embedded ZIVA surface (route reached AND in a WebView). */
export function isMobileHostActive(): boolean {
  return embedActive;
}

function postToNative(message: ZivaWebToNativeMessage): void {
  try {
    window.ReactNativeWebView?.postMessage(JSON.stringify(message));
  } catch {
    /* the app is not listening (e.g. opened in a plain browser) — no-op */
  }
}

export const zivaNative = {
  ready: () => postToNative({ type: "ziva:ready" }),
  tokenExpired: () => postToNative({ type: "ziva:token-expired" }),
  close: () => postToNative({ type: "ziva:close" }),
  unread: (count: number) => postToNative({ type: "ziva:unread", count }),
  session: (id: string) => postToNative({ type: "ziva:session", id }),
  error: (code?: string, message?: string) => postToNative({ type: "ziva:error", code, message }),
};

function isVersionOk(value: Record<string, unknown>): boolean {
  // Absent version = v1 (the field was added defensively); otherwise it must match.
  return value.v === undefined || value.v === ZIVA_BRIDGE_VERSION;
}

/**
 * Reads and validates the pre-boot bootstrap payload. Returns null when it is missing
 * (the deterministic case on Android, where the `ziva:init` handshake fills in instead)
 * or malformed.
 */
export function readZivaBootstrap(): ZivaBootstrap | null {
  const raw = window.__ZIVA_BOOTSTRAP__;
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Record<string, unknown>;
  if (!isVersionOk(value)) return null;
  if (typeof value.token !== "string" || !value.token) return null;
  if (typeof value.cspUrl !== "string" || !value.cspUrl) return null;
  return {
    token: value.token,
    cspUrl: value.cspUrl,
    insets: normalizeInsets(value.insets),
  };
}

function normalizeInsets(value: unknown): ZivaInsets | undefined {
  if (!value || typeof value !== "object") return undefined;
  const v = value as Record<string, unknown>;
  const top = typeof v.top === "number" ? v.top : 0;
  const bottom = typeof v.bottom === "number" ? v.bottom : 0;
  return { top, bottom };
}

export type ZivaNativeHandlers = {
  onInit?: (token: string, cspUrl: string, insets?: ZivaInsets) => void;
  onToken?: (token: string) => void;
  onLogout?: () => void;
  onBack?: () => void;
};

/**
 * Installs `window.__zivaBridge.receive` so the app can push native -> web messages.
 * Returns a disposer that removes the handler. Every payload is treated as untrusted:
 * unknown shapes and version mismatches are dropped.
 */
export function installZivaNativeBridge(handlers: ZivaNativeHandlers): () => void {
  const receive = (message: unknown) => {
    if (!message || typeof message !== "object") return;
    const msg = message as Record<string, unknown>;
    if (!isVersionOk(msg)) return;
    switch (msg.type) {
      case "ziva:init":
        if (typeof msg.token === "string" && typeof msg.cspUrl === "string") {
          handlers.onInit?.(msg.token, msg.cspUrl, normalizeInsets(msg.insets));
        }
        break;
      case "ziva:token":
        if (typeof msg.token === "string") handlers.onToken?.(msg.token);
        break;
      case "ziva:logout":
        handlers.onLogout?.();
        break;
      case "ziva:back":
        handlers.onBack?.();
        break;
      default:
        break;
    }
  };
  window.__zivaBridge = { receive };
  return () => {
    if (window.__zivaBridge?.receive === receive) {
      delete window.__zivaBridge;
    }
  };
}
